import { FC, useEffect, useState } from 'react';
import { SchedulingBackgroundFormFormValues, SchedulingBackgroundFormProps } from './SchedulingBackgroundForm.types';
import { Button, DropdownItem, FormField, SelectA11y } from '@base-app';
import { Sizes, Variants } from '@/common/types';
import {
  StyledForm,
  StyledSelectorsContainer,
  StyledTitle,
  formFieldStyles,
  sendButtonStyles,
} from './SchedulingBackgroundForm.styles';
import { useTranslation } from 'react-i18next';
import { GENETIC_COUNSELING_TRANSLATION } from '../../constants';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';
import { stateOptions } from '../../constants/states';
import { languageOptions } from '@/modules/genetic-counseling/constants/languages';
import useTimezones from '../../hooks/useTimezones';

const SchedulingBackgroundForm: FC<SchedulingBackgroundFormProps> = ({
  state,
  timezoneId,
  language,
  handleFormSubmit,
}) => {
  const { t } = useTranslation(GENETIC_COUNSELING_TRANSLATION);
  const [timezone, setTimezone] = useState<DropdownItem | undefined>(undefined);
  const { timezones: usTimezones } = useTimezones();

  const {
    handleSubmit,
    control,
    setValue,
    formState: { dirtyFields },
  } = useForm<SchedulingBackgroundFormFormValues>({
    mode: 'onChange',
    defaultValues: { state: undefined, timezone: undefined, language: undefined },
  });

  useEffect(() => {
    if (state !== undefined) {
      stateOptions.forEach((option) => {
        if (option.id === state) {
          setValue('state', state, { shouldDirty: true });
        }
      });
    }
    if (timezoneId !== undefined) {
      usTimezones.forEach((option) => {
        if (option.id === timezoneId) {
          setValue('timezone', timezoneId, { shouldDirty: true });
        }
      });
    }
    if (language !== undefined) {
      languageOptions.forEach((option) => {
        if (option.id === language) {
          setValue('language', language, { shouldDirty: true });
        }
      });
    }
  }, []);

  const handleSubmitButtonClick: SubmitHandler<SchedulingBackgroundFormFormValues> = (values): void => {
    handleFormSubmit(values);
  };

  return (
    <>
      <StyledTitle data-testid="scheduling-background-form-title">{t('scheduler.scheduling_background')}</StyledTitle>
      <StyledForm data-testid="scheduling-background-form" onSubmit={handleSubmit(handleSubmitButtonClick)}>
        <StyledSelectorsContainer>
          <FormField css={formFieldStyles}>
            <Controller
              control={control}
              name="state"
              render={({ field }) => {
                const item = field.value as unknown as string;
                return (
                  <SelectA11y
                    {...field}
                    defaultValue={item || ''}
                    fullWidth
                    id={field.name}
                    items={stateOptions}
                    label={t('scheduler.state')}
                    listWidth="100%"
                    name={field.name}
                    placeholder={t('scheduler.select_state')}
                    testId="scheduling-background-form-state"
                    value={item || ''}
                    variant={Variants.PRIMARY}
                    onChangeDropdownItem={(state) => {
                      usTimezones.forEach((timezone) => {
                        if (timezone.id === state.optionType) {
                          setTimezone(timezone);
                          setValue('timezone', timezone.id, { shouldDirty: true });
                        }
                      });
                    }}
                  />
                );
              }}
            />
          </FormField>
          <FormField css={formFieldStyles}>
            <Controller
              control={control}
              name="timezone"
              render={({ field }) => {
                const item = field.value as unknown as string;
                return (
                  <SelectA11y
                    {...field}
                    defaultValue={(timezone?.id ?? item) || ''}
                    fullWidth
                    id={field.name}
                    items={usTimezones}
                    label={t('scheduler.timezone')}
                    listWidth="100%"
                    name={field.name}
                    placeholder={t('scheduler.select_timezone')}
                    testId="scheduling-background-form-timezone"
                    value={(timezone?.id ?? item) || ''}
                    variant={Variants.PRIMARY}
                  />
                );
              }}
            />
          </FormField>
          <FormField css={formFieldStyles}>
            <Controller
              control={control}
              name="language"
              render={({ field }) => {
                const item = field.value as unknown as string;
                return (
                  <SelectA11y
                    {...field}
                    defaultValue={item || ''}
                    fullWidth
                    id={field.name}
                    items={languageOptions}
                    label={t('scheduler.language')}
                    listWidth="100%"
                    name={field.name}
                    placeholder={t('scheduler.select_language')}
                    testId="scheduling-background-form-language"
                    value={item || ''}
                    variant={Variants.TERTIARY}
                  />
                );
              }}
            />
          </FormField>
        </StyledSelectorsContainer>
        <Button
          css={sendButtonStyles}
          size={Sizes.MEDIUM}
          type="submit"
          disabled={!(dirtyFields.state && dirtyFields.timezone && dirtyFields.language)}
        >
          {t('scheduler.proceed_to_scheduling')}
        </Button>
      </StyledForm>
    </>
  );
};

export default SchedulingBackgroundForm;
